import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SessionUser } from '@apen/shared';
import { Avatar } from '../Avatar/Avatar';
import s from './AccountDrawer.module.css';

/**
 * Painel da conta, aberto pelo chip do perfil no cabeçalho.
 *
 * Fecha com Esc, com clique no fundo e ao navegar. O foco vai para o painel
 * na abertura e volta para o chip no fechamento — sem isso, quem usa teclado
 * abriria o painel e continuaria com o foco perdido atrás dele.
 */
export function AccountDrawer({
  usuario,
  aberto,
  onFechar,
  onSair,
}: {
  usuario: SessionUser | null | undefined;
  aberto: boolean;
  onFechar: () => void;
  onSair: () => void;
}) {
  const painel = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  useEffect(() => {
    if (!aberto) return;

    painel.current?.focus();

    function aoTeclar(event: KeyboardEvent) {
      if (event.key === 'Escape') onFechar();
    }
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, onFechar]);

  if (!aberto) return null;

  function ir(destino: string) {
    onFechar();
    void navigate(destino);
  }

  const ehAdmin = usuario?.role === 'admin';

  return (
    <div
      className={s.backdrop}
      role="presentation"
      onClick={(event) => {
        if (event.target === event.currentTarget) onFechar();
      }}
    >
      <div
        ref={painel}
        className={s.painel}
        role="dialog"
        aria-modal="true"
        aria-label="Conta"
        tabIndex={-1}
      >
        <button type="button" className={s.fechar} aria-label="Fechar" onClick={onFechar}>
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        <div className={s.identidade}>
          <Avatar
            className={s.avatar}
            src={usuario?.avatar_path}
            nome={usuario?.name}
            tamanho={42}
          />
          <span className={s.identidadeTexto}>
            <span className={s.nome}>{usuario?.name ?? '—'}</span>
            <span className={s.email}>{usuario?.email ?? ''}</span>
          </span>
        </div>

        <div className={s.itens}>
          <button type="button" className={s.item} onClick={() => { ir('/perfil'); }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="8" r="3.4" />
              <path d="M5 20a7 7 0 0 1 14 0" />
            </svg>
            <span>Perfil</span>
          </button>

          {/* Só admin gerencia contas; oferecer o atalho a quem receberia 403
              seria um beco sem saída. */}
          {ehAdmin ? (
            <button type="button" className={s.item} onClick={() => { ir('/usuarios'); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <circle cx="9" cy="8" r="3.2" />
                <path d="M2.5 20a6.5 6.5 0 0 1 13 0" />
                <path d="M16 5.2a3.2 3.2 0 0 1 0 6" />
                <path d="M18 14.4a6 6 0 0 1 3.5 5.6" />
              </svg>
              <span>Meus usuários</span>
            </button>
          ) : null}

          {ehAdmin ? (
            <button type="button" className={s.item} onClick={() => { ir('/bancos'); }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                <path d="M3 9.5 12 4l9 5.5" />
                <path d="M5 10v8M9.7 10v8M14.3 10v8M19 10v8" />
                <path d="M3 20h18" />
              </svg>
              <span>Bancos</span>
            </button>
          ) : null}
        </div>

        {/* Bancos e uma area propria, nao um formulario aqui dentro: a lista
            precisa de largura de tabela, e o cadastro ganhou busca no catalogo.
            O atalho fica no painel da conta porque o seletor de banco esta no
            cabecalho, a um palmo daqui. */}
        <div className={s.rodape}>
          <button type="button" className={s.sair} onClick={onSair}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M15 17l5-5-5-5" />
              <path d="M20 12H9" />
              <path d="M9 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h3" />
            </svg>
            <span>Sair</span>
          </button>
        </div>
      </div>
    </div>
  );
}
